/**
 * useCases — fetch + adapt the live `/api/cases` response into the existing
 * `Case` shape that the screens already consume. Lets us swap the demo
 * sampleData for live data without refactoring four screens.
 *
 * Fields the API doesn't yet return (visits/caregivers/hours, payerSplit,
 * audit metadata, assignee, manualRate, ownerEffectiveDate, daysGrandfather)
 * are filled with sensible defaults. Audit engine + future API revisions
 * will populate them.
 */

import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { Case, CaseState, FlagKind, FlagSeverity } from './sampleData';
import { FLAG_TYPES, parseDateOnly } from './sampleData';

interface ApiCase {
  id: number;
  franchisee_af: string;
  franchisee_name: string | null;
  axiscare_client_id: string;
  client_name: string | null;
  client_status: string | null;
  client_status_active?: boolean | number | null;
  location: string | null;
  client_street: string | null;
  client_zip: string | null;
  start_of_care: string | null;
  effective_end_date: string | null;
  owner_af: string | null;
  owner_name: string | null;
  servicing_fbc_name?: string | null;
  servicing_fbc_email?: string | null;
  owner_fbc_name?: string | null;
  owner_fbc_email?: string | null;
  owner_effective_date: string | null;
  owner_effective_date_source?: string | null;
  owner_status?: string | null;
  owner_not_yet_open?: boolean;
  owner_fdd_era?: 'pre2026' | 'fdd2026' | 'unknown' | null;
  visits_count: number;
  caregivers_count: number;
  total_visit_minutes: number;
  manual_rate_bps: number;
  longest_gap_days: number | null;
  audit_runtime_ms: number | null;
  audit_run_at?: string | null;
  assignee: { id: number; name: string | null; email: string | null } | null;
  dominant_zip: string;
  z_eval_source: 'dominant_clock_in' | 'patient_record_zip';
  zip_only_tag: boolean;
  high_manual_rate_tag: boolean;
  flags: { kind: FlagKind; details?: Record<string, unknown> }[];
  state: CaseState;
  considered_evidence: { kind: string; ref: string; outcome: string; reason: string }[];
  total_revenue_cents: number | null;
  payer_breakdown: { payer: string; zip: string; visits: number; minutes: number; revenueCents: number }[];
  notes: string | null;
  notes_updated_at: string | null;
  opened_at: string;
  last_state_change_at: string;
  last_evaluated_audit_run_id: number | null;
  spec_version_at_decision: string | null;
}

interface ApiCasesResponse {
  cases: ApiCase[];
  total: number;
  limit: number;
  offset: number;
}

export interface UseCasesResult {
  cases: Case[];
  /** Server-side total matching the query. Always equals cases.length once loaded. */
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Loads EVERY case matching the query, not a first page. The server caps a
 * single response at PAGE_SIZE rows, so this pages through by offset until it
 * has all `total` of them — a nationwide run (#79 opened 3,789 cases) used to
 * silently render only the first 500, which reads as "that's all there is".
 *
 * @param auditRunId When set, scopes the fetch to the cases that audit run
 * opened/updated (server joins audit_run_pair_evaluations). Changing it
 * refetches from the server.
 */
/** Rows per request. Must not exceed the server's `limit` ceiling in routes/cases.ts. */
const PAGE_SIZE = 1000;

export function useCases(auditRunId?: number | null): UseCasesResult {
  const [cases, setCases] = useState<Case[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const fetchAllPages = async () => {
      const all: ApiCase[] = [];
      let reportedTotal = 0;
      for (;;) {
        const qs = new URLSearchParams({
          limit: String(PAGE_SIZE),
          offset: String(all.length),
        });
        if (auditRunId) qs.set('audit_run_id', String(auditRunId));
        const res = await api<ApiCasesResponse>(`/api/cases?${qs.toString()}`);
        if (cancelled) return;
        reportedTotal = res.total;
        all.push(...res.cases);
        // Stop on a short/empty page as well as on reaching the reported
        // total: if `total` were ever stale relative to the rows themselves,
        // an empty page is the honest end of the data, not a reason to spin.
        if (res.cases.length === 0 || all.length >= reportedTotal) break;
      }
      if (cancelled) return;
      setCases(all.map(adapt));
      setTotal(reportedTotal);
      setLoading(false);
    };

    fetchAllPages().catch((err: unknown) => {
      if (cancelled) return;
      setError(err instanceof Error ? err.message : 'Failed to load cases');
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [tick, auditRunId]);

  return { cases, total, loading, error, refresh: () => setTick((t) => t + 1) };
}

// Exposed for fetch-on-miss in App: a case opened from global search (or any
// id outside the loaded window) is fetched via GET /api/cases/:id — the same
// row shape as the list — and adapted with the same function.
export type { ApiCase };

// Q4 grandfather from the latest evaluation's evidence: an effective_date
// "cleared" entry, not voided by a later "rejected" one (Q4b break in service).
function legacyOf(ev: ApiCase['considered_evidence'] | null | undefined): { reason: string } | null {
  const rows = (ev ?? []).filter((e) => e.kind === 'effective_date');
  const cleared = rows.find((e) => e.outcome === 'cleared');
  if (!cleared || rows.some((e) => e.outcome === 'rejected')) return null;
  return { reason: cleared.reason };
}

export function adaptApiCase(c: ApiCase): Case {
  return adapt(c);
}

function adapt(c: ApiCase): Case {
  const flags: FlagKind[] = (c.flags ?? []).map((f) => f.kind).filter(Boolean) as FlagKind[];
  // Keep the engine's real per-flag detail payloads (recordZip, shares, zips…)
  // so the case detail can show actual evidence instead of placeholder copy.
  const flagDetails: Partial<Record<FlagKind, Record<string, unknown>>> = {};
  for (const f of c.flags ?? []) {
    if (f?.kind && f.details && typeof f.details === 'object') flagDetails[f.kind] = f.details;
  }
  const sev: FlagSeverity = computeSev(flags);
  const opened = new Date(c.opened_at);
  return {
    id: `CASE-${c.id}`,
    patient: c.client_name ?? '(unknown client)',
    clientStatus: c.client_status ?? null,
    clientActive: c.client_status_active == null ? null : Number(c.client_status_active) === 1,
    legacy: legacyOf(c.considered_evidence),
    axiscareClientId: c.axiscare_client_id,
    location: c.location ?? '',
    franchiseeAf: c.franchisee_af,
    franchiseeName: c.franchisee_name ?? c.franchisee_af,
    // Owner of dominant_zip: not currently joined in the API response.
    // Decision tree records `Q3_own_territory` vs `Q2_zip_unowned` via
    ownerAf: c.owner_af,
    ownerName: c.owner_name,
    ownerStatus: c.owner_status ?? null,
    ownerNotYetOpen: !!c.owner_not_yet_open,
    ownerFddEra: c.owner_fdd_era ?? null,
    servicingFbcName: c.servicing_fbc_name ?? null,
    servicingFbcEmail: c.servicing_fbc_email ?? null,
    ownerFbcName: c.owner_fbc_name ?? null,
    ownerFbcEmail: c.owner_fbc_email ?? null,
    dominantZip: c.dominant_zip,
    zipOnlyTag: c.zip_only_tag,
    manualRate: (c.manual_rate_bps ?? 0) / 10_000,
    longestGapDays: c.longest_gap_days,
    flags,
    flagDetails,
    sev,
    state: c.state,
    totalRevenueCents: c.total_revenue_cents ?? 0,
    payerSplit: aggregatePayerSplit(c.payer_breakdown ?? []),
    payerBreakdown: c.payer_breakdown ?? [],
    visits: c.visits_count ?? 0,
    caregivers: c.caregivers_count ?? 0,
    hours: (c.total_visit_minutes ?? 0) / 60,
    // Date-only values read as calendar days (parseDateOnly), and left null
    // when missing — they used to fall back to the case's opened date.
    startOfCare: parseDateOnly(c.start_of_care),
    ownerEffectiveDate: parseDateOnly(c.owner_effective_date),
    ownerEffectiveDateSource: c.owner_effective_date_source ?? null,
    // Days between owner effective date and patient SOC. Per spec Q4:
    //   negative = grandfathered (patient came BEFORE owner took over the territory)
    //   positive = patient came AFTER owner effective date (no grandfather)
    daysGrandfather: c.start_of_care && c.owner_effective_date
      ? Math.round((parseDateOnly(c.start_of_care)!.getTime() - parseDateOnly(c.owner_effective_date)!.getTime()) / 86_400_000)
      : null,
    // Null-through, not defaults: a value the API didn't provide must render
    // as missing ("—"), never as a plausible fabrication (the 7/9 audit found
    // "Runtime 0.0s", "Spec v0.6.0", and "triggered by system" showing on
    // cases where none of that was recorded).
    audit: {
      runId: c.last_evaluated_audit_run_id ? `RUN-${c.last_evaluated_audit_run_id}` : null,
      specVersion: c.spec_version_at_decision ?? null,
      runtimeMs: c.audit_runtime_ms ?? null,
      triggeredBy: null,   // per-run triggerer isn't on the case API yet — hide, don't invent
      triggeredAt: opened,
    },
    auditRunAt: c.audit_run_at ? new Date(c.audit_run_at) : null,
    assignee: c.assignee,
    openedAt: opened,
  };
}

// Aggregate per-payer × per-zip rows into a per-payer revenue % map keyed by
// AxisCare's own payer name, largest share first. Deliberately NO bucketing
// into VA / Medicaid / LTCi / Private: the old substring match sent "Private
// Pay" to VA ("pri-va-te") and every unrecognised payer (Optum, John Hancock,
// OLTL, ...) silently to Private (Bettina, 2026-10-19). Categories, if wanted
// back, need an explicit owned mapping with an "Unclassified" bucket.
export const NO_PAYER_LABEL = '(no payer recorded)';
function aggregatePayerSplit(rows: { payer: string; revenueCents: number }[]): Record<string, number> {
  const totals = new Map<string, number>();
  let grand = 0;
  for (const r of rows) {
    const name = (r.payer ?? '').trim() || NO_PAYER_LABEL;
    totals.set(name, (totals.get(name) ?? 0) + r.revenueCents);
    grand += r.revenueCents;
  }
  if (grand === 0) return {};
  const pct: Record<string, number> = {};
  for (const [name, cents] of [...totals].sort((a, b) => b[1] - a[1])) {
    if (cents > 0) pct[name] = Math.round((cents / grand) * 100);
  }
  return pct;
}

function computeSev(flags: FlagKind[]): FlagSeverity {
  if (flags.length === 0) return 'ok';
  let worst: FlagSeverity = 'ok';
  const order: FlagSeverity[] = ['ok', 'info', 'gold', 'warn', 'danger'];
  for (const f of flags) {
    const s = FLAG_TYPES[f]?.sev ?? 'info';
    if (order.indexOf(s) > order.indexOf(worst)) worst = s;
  }
  return worst;
}
